import { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { reconcileSession } from '../lib/auth-session';
import { useAuthStore } from '../stores/auth.store';

/**
 * Initializes Supabase auth state on app mount and subscribes to changes.
 * Call this once in your root component.
 */
export function useAuthInit(): void {
  useEffect(() => {
    const hydrateStoredSession = () => {
      void supabase.auth.getSession().then(({ data: { session } }) => {
        void reconcileSession(session);
      });
    };

    const handleWake = () => {
      if (document.visibilityState === 'hidden') return;
      hydrateStoredSession();
    };

    // Hydrate from existing session on page load
    hydrateStoredSession();

    // Subscribe to auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void reconcileSession(session);
    });

    document.addEventListener('visibilitychange', handleWake);
    window.addEventListener('focus', handleWake);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleWake);
      window.removeEventListener('focus', handleWake);
    };
  }, []);
}

/**
 * Returns current auth state.
 */
export function useAuth() {
  const { user, session, isLoading } = useAuthStore();
  return {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
  };
}
